import {
    FileTextOutlined,
    InfoCircleOutlined,
    PrinterOutlined,
    TagOutlined,
    TrophyOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Input,
    message,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Typography,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import ProgressModal from '../components/ProgressModal';
import {
    canPair,
    canPrintLabels,
    needsPairing,
    pairingLostFromErrorCode,
    printContractLabels,
    probeHardwareService,
    type HardwareServiceStatus,
} from '../domain/labelPrinting';
import { usePrinterSelection } from '../hooks/usePrinterSelection';
import { loadContractLabelJobs } from '../utils/contractLabels';
import { pairHardwareService } from '../utils/hardwareServicePairing';
import { useContractDocument } from '../utils/contractDocument';
import { parseContractReference } from '../domain/contractReference';

const { Title, Text } = Typography;

interface TombolaData {
    id: number;
    contractNr: number;
    contractNumberStr: string | null;
    clientNr: number;
    clientName: string | null;
    stamp: string | null;
    sumaTombola: number;
    contractDate: string | null;
}

interface AnnexItem {
    id: number;
    denumire: string;
    isBool: boolean;
    boolvalue: boolean;
    valoare: string | null;
    tip: string;
}

const Listings = () => {
    const { t } = useTranslation();
    const { API_BASE_URL, token } = useAuth();
    // The cash point this workstation is registered as, resolved by App.tsx from the
    // station name. Used only to pre-select the pairing target.
    const { cashPointId: registeredCashPointId } = useCashDrawer();
    const { downloadPdfDocument } = useContractDocument(t);

    // Contract reprint state
    const [contractNrInput, setContractNrInput] = useState('');
    const [contractLoading, setContractLoading] = useState(false);
    const [progressModalOpen, setProgressModalOpen] = useState(false);
    const [progressValue, setProgressValue] = useState(0);
    const [progressStatus, setProgressStatus] = useState<
        'preparing' | 'processing' | 'completed' | 'error'
    >('preparing');
    const [progressMessage, setProgressMessage] = useState('');

    // Invoice reprint state
    const [invoiceNrInput, setInvoiceNrInput] = useState('');
    const [invoiceLoading, setInvoiceLoading] = useState(false);

    // Electronics annex state
    const [annexContractNrInput, setAnnexContractNrInput] = useState('');
    const [annexLoading, setAnnexLoading] = useState(false);
    const [annexModalOpen, setAnnexModalOpen] = useState(false);
    const [annexData, setAnnexData] = useState<AnnexItem[]>([]);
    const [annexContractNr, setAnnexContractNr] = useState('');

    // Tombola state
    const [tombolaContractNrInput, setTombolaContractNrInput] = useState('');
    const [tombolaLoading, setTombolaLoading] = useState(false);
    const [tombolaModalOpen, setTombolaModalOpen] = useState(false);
    const [tombolaData, setTombolaData] = useState<TombolaData | null>(null);

    // Contract label state
    const [labelContractNrInput, setLabelContractNrInput] = useState('');
    const [labelLoading, setLabelLoading] = useState(false);
    // A ref, not `labelLoading`: two triggers in the same tick both still read the old state.
    const labelPrintRunning = useRef(false);
    const [labelServiceStatus, setLabelServiceStatus] = useState<HardwareServiceStatus | null>(
        null,
    );
    const [cashPoints, setCashPoints] = useState<{ id: number; name: string }[]>([]);
    const [pairingCashPointId, setPairingCashPointId] = useState<number | null>(null);
    const [pairingBusy, setPairingBusy] = useState(false);

    // The same hook Configure → Printers uses, over the same local-service preference. Two views,
    // one source of truth: a selection made here shows up there and vice versa, and neither screen
    // carries its own copy of the rules about what a printer state means.
    const printers = usePrinterSelection({ enabled: labelServiceStatus?.state === 'ready' });

    const authHeaders = {
        Authorization: `Bearer ${token}`,
    };

    const openBlobInNewTab = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handlePrintContract = async () => {
        const nr = parseInt(contractNrInput, 10);
        if (isNaN(nr) || nr <= 0) {
            message.error(t('listings.invalidContractNumber'));
            return;
        }
        setContractLoading(true);
        setProgressModalOpen(true);
        setProgressValue(0);
        setProgressStatus('preparing');
        setProgressMessage('');
        try {
            await downloadPdfDocument(nr, contractNrInput, (progress, status, msg) => {
                setProgressValue(progress);
                setProgressStatus(status);
                if (msg) setProgressMessage(msg);
            });
            setTimeout(() => setProgressModalOpen(false), 1500);
        } catch (error) {
            message.error(
                error instanceof Error ? error.message : t('listings.contractFetchError'),
            );
            setTimeout(() => setProgressModalOpen(false), 2000);
        } finally {
            setContractLoading(false);
        }
    };

    const handlePrintInvoice = async () => {
        const invoiceNr = invoiceNrInput.trim();
        if (!invoiceNr) {
            message.error(t('listings.invalidInvoiceNumber'));
            return;
        }
        setInvoiceLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/payments/invoice/${encodeURIComponent(invoiceNr)}`,
                {
                    method: 'GET',
                    headers: authHeaders,
                },
            );
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(
                    (data as { message?: string }).message ?? t('listings.invoiceFetchError'),
                );
            }
            const blob = await response.blob();
            openBlobInNewTab(blob, `Chitanta_${invoiceNr.padStart(6, '0')}.pdf`);
        } catch (error) {
            message.error(error instanceof Error ? error.message : t('listings.invoiceFetchError'));
        } finally {
            setInvoiceLoading(false);
        }
    };

    const handleViewAnnex = async () => {
        const nr = parseInt(annexContractNrInput, 10);
        if (isNaN(nr) || nr <= 0) {
            message.error(t('listings.invalidContractNumber'));
            return;
        }
        setAnnexLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/electronics-annex/contract/${nr}`, {
                method: 'GET',
                headers: authHeaders,
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(
                    (data as { error?: string }).error ?? t('listings.annexFetchError'),
                );
            }
            const data = (await response.json()) as { annexData: AnnexItem[] };
            if (!data.annexData || data.annexData.length === 0) {
                message.warning(t('listings.annexNotFound'));
                return;
            }
            setAnnexData(data.annexData);
            setAnnexContractNr(String(nr));
            setAnnexModalOpen(true);
        } catch (error) {
            message.error(error instanceof Error ? error.message : t('listings.annexFetchError'));
        } finally {
            setAnnexLoading(false);
        }
    };

    const handlePrintTombola = async () => {
        const nr = parseInt(tombolaContractNrInput, 10);
        if (isNaN(nr) || nr <= 0) {
            message.error(t('listings.invalidContractNumber'));
            return;
        }
        setTombolaLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/listings/tombola/${nr}`, {
                method: 'GET',
                headers: authHeaders,
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(
                    (data as { message?: string }).message ?? t('listings.tombolaFetchError'),
                );
            }
            const data = (await response.json()) as { success: boolean; data: TombolaData };
            setTombolaData(data.data);
            setTombolaModalOpen(true);
        } catch (error) {
            message.error(error instanceof Error ? error.message : t('listings.tombolaFetchError'));
        } finally {
            setTombolaLoading(false);
        }
    };

    // Probe the workstation service once, so the card can say what is wrong before the
    // operator types a contract number and presses a button that cannot work.
    useEffect(() => {
        let cancelled = false;

        // Printer discovery is not done here any more: `usePrinterSelection` owns it, and starts
        // as soon as this probe reports the service ready.
        void probeHardwareService(window.fetch.bind(window)).then(status => {
            if (!cancelled) {
                setLabelServiceStatus(status);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    // Cash points are only needed to answer "which counter is this workstation", which
    // is asked once, at pairing.
    useEffect(() => {
        if (!canPair(labelServiceStatus)) {
            return;
        }

        let cancelled = false;

        void fetch(`${API_BASE_URL}/cash-drawer/cashpoints`, { headers: authHeaders })
            .then(response => (response.ok ? response.json() : []))
            .then((points: { id: number; name: string }[]) => {
                if (!cancelled) {
                    setCashPoints(points);
                    // Defaults to the cash point this workstation is already registered
                    // as, so the common case is a confirmation rather than a decision.
                    // Still shown and changeable: pairing is a security binding, and the
                    // registration behind that default is a name the operator typed.
                    setPairingCashPointId(
                        current => current ?? registeredCashPointId ?? points[0]?.id ?? null,
                    );
                }
            })
            .catch(() => {
                // A failed lookup leaves the selector empty; the card still explains
                // that the workstation is unpaired.
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labelServiceStatus, API_BASE_URL, token, registeredCashPointId]);

    /**
     * Pair this workstation with the cash point the operator picked.
     *
     * Deliberately an explicit action, not automatic: pairing binds this machine to a
     * physical counter, and only a person standing at it knows which one. Guessing
     * would silently route a shop's labels to another shop's printer.
     */
    const handlePairWorkstation = async () => {
        if (pairingCashPointId === null) {
            return;
        }

        setPairingBusy(true);
        try {
            const browserFetch = window.fetch.bind(window);
            const outcome = await pairHardwareService(
                API_BASE_URL,
                authHeaders,
                pairingCashPointId,
                browserFetch,
            );

            if (!outcome.paired) {
                message.error(t('listings.label.pairingFailed'));
                return;
            }

            setLabelServiceStatus(await probeHardwareService(browserFetch));
            await printers.reload(true);
            message.success(t('listings.label.paired'));
        } finally {
            setPairingBusy(false);
        }
    };

    /** Whether the card should warn instead of offering to print. */
    const labelServiceUsable = (status: HardwareServiceStatus): boolean =>
        canPrintLabels(status) && !needsPairing(status);

    /** Explains, in the operator's language, why label printing is not usable here. */
    const labelServiceMessage = (status: HardwareServiceStatus): string => {
        switch (status.state) {
            case 'unavailable':
                return t('listings.label.serviceUnavailable');
            case 'unrecognised':
                return t('listings.label.serviceUnrecognised');
            case 'incompatible':
                return t('listings.label.serviceIncompatible');
            case 'unpaired':
                return t('listings.label.notPaired');
            default:
                // Reachable, current and connected, so the only thing left is that this
                // build has no print transport for the hardware on this platform.
                return t('listings.label.capabilityMissing');
        }
    };

    /** Maps a backend or service error code onto a translated operator message. */
    const labelErrorMessage = (code: string | undefined): string => {
        const messages: Record<string, string> = {
            CONTRACT_NOT_FOUND: t('listings.label.contractNotFound'),
            NO_PRINTER_CONFIGURED: t('listings.label.noPrinterConfigured'),
            UNSUPPORTED_LABEL_STOCK: t('listings.label.unsupportedStock'),
            PRINTER_NOT_FOUND: t('listings.label.printerNotFound'),
            CAPABILITY_MISSING: t('listings.label.capabilityMissing'),
            LABEL_CONTENT_EXCEEDS_MEDIA: t('listings.label.unsupportedStock'),
            PRINTER_UNAVAILABLE: t('listings.label.printerUnavailable'),
            SERVICE_UNREACHABLE: t('listings.label.serviceUnavailable'),
            NOT_PAIRED: t('listings.label.notPaired'),
            INSTALLATION_NOT_PAIRED: t('listings.label.notPaired'),
            INSTALLATION_REVOKED: t('listings.label.revoked'),
        };

        return messages[code ?? ''] ?? t('listings.label.buildFailed');
    };

    /**
     * Report a label failure, and let a lost pairing put the pairing control back.
     *
     * The service answers every probe as a healthy, paired workstation when only the *backend's*
     * half of the pairing is gone - correctly, about itself - so the status alone can never learn
     * this. Without the correction the operator is told the workstation is not paired and offered
     * no way to pair it, which is a dead end: recovering meant an administrator deleting the
     * service's state file. The backend's error code is the only evidence this page gets.
     */
    const reportLabelFailure = (code: string | undefined): void => {
        message.error(labelErrorMessage(code));

        if (pairingLostFromErrorCode(code)) {
            setLabelServiceStatus({ state: 'unpaired' });
        }
    };

    const handleSelectPrinter = async (printerId: string) => {
        const ok = await printers.choose(printerId);

        message[ok ? 'success' : 'error'](
            t(ok ? 'listings.label.printerSelected' : 'listings.label.printerSelectFailed'),
        );
    };

    // One run at a time. Every run fetches fresh idempotency keys, so a second run started while
    // the first is still going is not a duplicate the service can suppress - it is a second set
    // of physical labels. A scanner repeating its Enter or a double click must not cause that.
    const handlePrintLabels = async () => {
        if (labelPrintRunning.current) {
            return;
        }

        labelPrintRunning.current = true;

        try {
            await printScannedLabels();
        } finally {
            labelPrintRunning.current = false;
        }
    };

    const printScannedLabels = async () => {
        // The reprint box is the input most likely to be driven by a scanner: an operator
        // holds an object whose tag is smudged and scans it. All three shapes are accepted
        // — a legacy label, a label this application printed, or a typed contract number.
        const scanned = parseContractReference(labelContractNrInput, { allowBareNumber: true });

        if (!scanned.ok) {
            message.error(t('listings.label.invalidContractNumber'));
            return;
        }

        const { contractNumber, objectLetter } = scanned.value;

        if (labelServiceStatus?.state !== 'ready') {
            message.error(t('listings.label.serviceUnavailable'));
            return;
        }

        const { installationId } = labelServiceStatus.health;

        // Ask the workstation which device it will use *before* the backend renders anything:
        // the renderer depends on the provider, and only the workstation can see its printers.
        const resolved = await printers.reload(true);

        const printerId = resolved?.selectedPrinterId ?? null;
        const provider = resolved?.selectedProvider ?? null;

        if (resolved === null || !resolved.ready || printerId === null || provider === null) {
            message.error(resolved?.statusReason ?? t('listings.label.printerUnavailable'));
            return;
        }

        // A canonical label identifies one pledged object, so scanning the damaged tag
        // reprints that tag rather than the whole contract's set. A contract number — typed
        // or from a legacy label — identifies no single object, so it prints them all, which
        // is what this box has always done.
        let objectMissing = false;

        setLabelLoading(true);
        try {
            // The backend renders and resolves the printer queue from this workstation's
            // pairing binding; the browser is only the transport, because a central
            // backend cannot reach a printer attached to this counter.
            const outcome = await printContractLabels(
                // The provider argument is how a cross-provider failover rebuilds: the service
                // refuses to put a Zebra payload on a DYMO, names the family to use, and this
                // reloads the plan for it. Falls back to the provider resolved before the run.
                async rebuildFor => {
                    const loaded = await loadContractLabelJobs(
                        API_BASE_URL,
                        authHeaders,
                        contractNumber,
                        installationId,
                        // The printer id is dropped on a rebuild: it named the device that just
                        // became unavailable, and the service approves the replacement itself.
                        rebuildFor === undefined
                            ? { provider, printerId }
                            : { provider: rebuildFor },
                    );

                    if (!loaded.ok || objectLetter === undefined) {
                        return loaded;
                    }

                    const jobs = loaded.jobs.filter(job => job.objectLetter === objectLetter);

                    // Only a contract that *has* objects but not this one is a mismatch; an
                    // empty contract is already reported on its own.
                    objectMissing = loaded.jobs.length > 0 && jobs.length === 0;

                    return { ok: true, jobs };
                },
                window.fetch.bind(window),
                labelServiceStatus,
            );

            if (outcome.kind === 'build-failed') {
                reportLabelFailure(outcome.code);
                return;
            }

            if (objectMissing) {
                message.error(
                    t('listings.label.objectNotOnContract', {
                        letter: objectLetter,
                        contract: contractNumber,
                    }),
                );
                return;
            }

            if (outcome.kind === 'no-objects') {
                message.info(t('listings.label.noObjects'));
                return;
            }

            const { summary } = outcome;

            // Non-blocking and after the fact: the labels are already out, and the operator needs
            // to know they came off a different printer than the one they had selected.
            if (outcome.failedOverToProvider !== undefined) {
                await printers.reload(true);
                message.warning(
                    t('listings.label.failedOverToProvider', {
                        provider: outcome.failedOverToProvider.toUpperCase(),
                    }),
                );
            }

            if (summary.failed > 0) {
                const failure = summary.results.find(result => result.outcome === 'failed');
                reportLabelFailure(failure?.errorCode);
                return;
            }

            if (summary.printed === 0) {
                message.info(t('listings.label.duplicateSuppressed'));
                return;
            }

            message.success(
                t('listings.label.printed', {
                    count: summary.printed,
                    letters: summary.printedLetters.join(', '),
                }),
            );
        } catch (error) {
            message.error(error instanceof Error ? error.message : t('listings.label.buildFailed'));
        } finally {
            setLabelLoading(false);
        }
    };

    const annexColumns = [
        {
            title: t('listings.annex.characteristic'),
            dataIndex: 'denumire',
            key: 'denumire',
            width: '50%',
        },
        {
            title: t('listings.annex.value'),
            key: 'value',
            render: (_: unknown, record: AnnexItem) => {
                if (!record.isBool) return record.valoare ?? '';
                return record.boolvalue ? t('listings.annex.yes') : t('listings.annex.no');
            },
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#2563eb' } as React.CSSProperties}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <PrinterOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('listings.title')}
                        </Title>
                        <Text type="secondary">{t('listings.subtitle')}</Text>
                    </div>
                </div>
            </div>

            <Row gutter={[16, 16]}>
                {/* Contract Reprinting */}
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                {t('listings.contract.title')}
                            </>
                        }
                    >
                        <p>{t('listings.contract.description')}</p>
                        <Input.Search
                            placeholder={t('listings.contract.placeholder')}
                            value={contractNrInput}
                            onChange={e => setContractNrInput(e.target.value)}
                            onSearch={handlePrintContract}
                            enterButton={
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    loading={contractLoading}
                                >
                                    {t('listings.print')}
                                </Button>
                            }
                            onPressEnter={handlePrintContract}
                        />
                    </Card>
                </Col>

                {/* Invoice Reprinting */}
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                {t('listings.invoice.title')}
                            </>
                        }
                    >
                        <p>{t('listings.invoice.description')}</p>
                        <Input.Search
                            placeholder={t('listings.invoice.placeholder')}
                            value={invoiceNrInput}
                            onChange={e => setInvoiceNrInput(e.target.value)}
                            onSearch={handlePrintInvoice}
                            enterButton={
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    loading={invoiceLoading}
                                >
                                    {t('listings.print')}
                                </Button>
                            }
                            onPressEnter={handlePrintInvoice}
                        />
                    </Card>
                </Col>

                {/* Electronics Annex */}
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                {t('listings.annex.title')}
                            </>
                        }
                    >
                        <p>{t('listings.annex.description')}</p>
                        <Input.Search
                            placeholder={t('listings.contract.placeholder')}
                            value={annexContractNrInput}
                            onChange={e => setAnnexContractNrInput(e.target.value)}
                            onSearch={handleViewAnnex}
                            enterButton={
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    loading={annexLoading}
                                >
                                    {t('listings.view')}
                                </Button>
                            }
                            onPressEnter={handleViewAnnex}
                        />
                    </Card>
                </Col>

                {/* Tombola */}
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <>
                                <TrophyOutlined style={{ marginRight: 8 }} />
                                {t('listings.tombola.title')}
                            </>
                        }
                    >
                        <p>{t('listings.tombola.description')}</p>
                        <Input.Search
                            placeholder={t('listings.contract.placeholder')}
                            value={tombolaContractNrInput}
                            onChange={e => setTombolaContractNrInput(e.target.value)}
                            onSearch={handlePrintTombola}
                            enterButton={
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    loading={tombolaLoading}
                                >
                                    {t('listings.print')}
                                </Button>
                            }
                            onPressEnter={handlePrintTombola}
                        />
                    </Card>
                </Col>

                {/* Contract object labels, printed via the workstation hardware service */}
                <Col xs={24} md={12}>
                    <Card
                        title={
                            <>
                                <TagOutlined style={{ marginRight: 8 }} />
                                {t('listings.label.title')}
                            </>
                        }
                    >
                        <Text type="secondary">{t('listings.label.description')}</Text>

                        {labelServiceStatus !== null && !labelServiceUsable(labelServiceStatus) && (
                            <Alert
                                style={{ marginTop: 12 }}
                                data-testid="label-service-alert"
                                type="warning"
                                icon={<InfoCircleOutlined />}
                                showIcon
                                message={labelServiceMessage(labelServiceStatus)}
                                description={
                                    labelServiceStatus.state === 'unavailable'
                                        ? t('listings.label.serviceUnavailableDescription')
                                        : undefined
                                }
                            />
                        )}

                        {canPair(labelServiceStatus) && (
                            <Space.Compact style={{ marginTop: 12, width: '100%' }}>
                                <Select
                                    style={{ width: '100%' }}
                                    data-testid="label-cashpoint-select"
                                    placeholder={t('listings.label.selectCashPoint')}
                                    value={pairingCashPointId ?? undefined}
                                    onChange={setPairingCashPointId}
                                    options={cashPoints.map(point => ({
                                        value: point.id,
                                        label: point.name,
                                    }))}
                                />
                                <Button
                                    type="primary"
                                    loading={pairingBusy}
                                    disabled={pairingCashPointId === null}
                                    data-testid="label-pair-button"
                                    onClick={handlePairWorkstation}
                                >
                                    {t('listings.label.pair')}
                                </Button>
                            </Space.Compact>
                        )}

                        {printers.view.rows.length > 0 && (
                            <div style={{ marginTop: 12 }} data-testid="label-printer-panel">
                                {!printers.view.canPrint && (
                                    <Alert
                                        style={{ marginBottom: 8 }}
                                        data-testid="label-printer-unavailable"
                                        type="warning"
                                        showIcon
                                        message={
                                            printers.view.blockedReason ??
                                            t('listings.label.printerUnavailable')
                                        }
                                    />
                                )}

                                <Select
                                    style={{ width: '100%' }}
                                    data-testid="label-printer-select"
                                    loading={printers.busy || printers.loading}
                                    placeholder={t('listings.label.selectPrinter')}
                                    value={printers.view.effectivePrinterId ?? undefined}
                                    onChange={handleSelectPrinter}
                                    options={printers.view.rows.map(row => ({
                                        value: row.id,
                                        // Only a printer the service can reach may be chosen;
                                        // offering an offline one writes a preference no device
                                        // satisfies, and it fails later, mid-transaction.
                                        disabled: !row.selectable && !row.effective,
                                        // Enough to tell two of the same model apart, and to say
                                        // which one is preferred versus actually in use.
                                        label:
                                            `${row.provider.toUpperCase()} — ${row.displayName}` +
                                            ` · ${t(
                                                `configuration.labelPrinter.health.${row.health}`,
                                            )}` +
                                            (row.preferred && !row.effective
                                                ? ` · ${t('configuration.labelPrinter.preferred')}`
                                                : ''),
                                    }))}
                                />
                            </div>
                        )}

                        <Input.Search
                            style={{ marginTop: 12 }}
                            data-testid="label-contract-input"
                            placeholder={t('listings.label.contractNumber')}
                            value={labelContractNrInput}
                            onChange={event => setLabelContractNrInput(event.target.value)}
                            enterButton={
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    loading={labelLoading}
                                    data-testid="label-print-button"
                                >
                                    {t('listings.label.print')}
                                </Button>
                            }
                            // `onSearch` alone: Input.Search already calls it on Enter, so an
                            // `onPressEnter` as well ran the print twice and every scan (a
                            // scanner ends with Enter) came out as two labels.
                            onSearch={handlePrintLabels}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Electronics Annex Modal */}
            <Modal
                title={`${t('listings.annex.modalTitle')} – ${t(
                    'listings.contract.number',
                )} ${annexContractNr}`}
                open={annexModalOpen}
                onCancel={() => setAnnexModalOpen(false)}
                footer={[
                    <Button
                        key="print"
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={() => window.print()}
                    >
                        {t('listings.print')}
                    </Button>,
                    <Button key="close" onClick={() => setAnnexModalOpen(false)}>
                        {t('listings.close')}
                    </Button>,
                ]}
                width={600}
            >
                <Divider />
                <Table
                    dataSource={annexData}
                    columns={annexColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    bordered
                />
            </Modal>

            {/* Tombola Modal */}
            <Modal
                title={t('listings.tombola.modalTitle')}
                open={tombolaModalOpen}
                onCancel={() => setTombolaModalOpen(false)}
                footer={[
                    <Button
                        key="print"
                        type="primary"
                        icon={<PrinterOutlined />}
                        onClick={() => window.print()}
                    >
                        {t('listings.print')}
                    </Button>,
                    <Button key="close" onClick={() => setTombolaModalOpen(false)}>
                        {t('listings.close')}
                    </Button>,
                ]}
                width={500}
            >
                {tombolaData && (
                    <>
                        <Divider />
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label={t('listings.tombola.id')}>
                                {tombolaData.id}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('listings.tombola.contractNr')}>
                                {tombolaData.contractNr}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('listings.tombola.contractNumber')}>
                                {tombolaData.contractNumberStr ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('listings.tombola.client')}>
                                {tombolaData.clientName ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('listings.tombola.date')}>
                                {tombolaData.contractDate ?? '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('listings.tombola.amount')}>
                                {tombolaData.sumaTombola?.toFixed(0)} Lei
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
                {!tombolaData && <Spin />}
            </Modal>

            <ProgressModal
                open={progressModalOpen}
                title={t('progress.pdfGeneration')}
                progress={progressValue}
                status={progressStatus}
                message={progressMessage}
                onClose={() => setProgressModalOpen(false)}
            />
        </div>
    );
};

export default Listings;
